import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@shared/ui';
import { useAuth } from '@entities/auth';
import {
  isPasswordPolicyMet,
  validatePasswordMatch,
  type PasswordPolicyRule,
} from '@entities/auth';
import { useChangePassword } from '../model/use-change-password';
import { useCooldown } from '@entities/auth';
import { passwordErrorMessage, type PasswordApiFailure } from '@entities/auth';
import { useSecondFactorAction } from '@features/security';
import { ApiError } from '@shared/api/errors/errors';
import { formatDateTime } from '@shared/lib/format-date-time';
import { PasswordChangeWarning } from '@entities/auth';
import { PasswordPolicyChecklist } from '@entities/auth';

interface ChangePasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordModal({ open, onOpenChange }: ChangePasswordModalProps) {
  const { t, i18n } = useTranslation('auth');
  const { toast } = useToast();
  const { user } = useAuth();
  const { changePassword, isSubmitting } = useChangePassword();
  const { run } = useSecondFactorAction();
  const { secondsLeft, start } = useCooldown();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [code, setCode] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [policyRules, setPolicyRules] = useState<PasswordPolicyRule[]>([]);
  const submitLock = useRef(false);

  const needsOtp = Boolean(user?.twoFactorEnabled) || showOtp;
  const hasApiToken = Boolean(user?.apiToken);
  const locked = isSubmitting || secondsLeft > 0;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setRepeatPassword('');
    setCode('');
    setShowOtp(false);
    setErrors({});
    setPolicyRules([]);
    submitLock.current = false;
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!currentPassword) {
      nextErrors.currentPassword = t('errors.passwordRequired');
    }
    if (!isPasswordPolicyMet(newPassword)) {
      nextErrors.newPassword = t('recovery.policyLabel');
    }
    const match = validatePasswordMatch(newPassword, repeatPassword);
    if (!match.isValid && match.error) {
      nextErrors.repeatPassword = t(match.error);
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const applyFailure = (failure: PasswordApiFailure) => {
    if (failure.showOtp) {
      setShowOtp(true);
    }
    if (failure.code === 'PASSWORD_POLICY') {
      setPolicyRules(failure.policyRules);
    }
    if (failure.code === 'TOO_MANY_REQUESTS' && failure.retryAfterSec) {
      start(failure.retryAfterSec);
    }
    const nextErrors: Record<string, string> = {};
    Object.entries(failure.fieldErrors).forEach(([path, key]) => {
      nextErrors[path] = t(key);
    });
    if (failure.code === 'CURRENT_PASSWORD_INVALID') {
      nextErrors.currentPassword = t('recovery.currentInvalid');
    }
    if (failure.code === 'PASSWORD_SAME') {
      nextErrors.newPassword = t('recovery.samePassword');
    }
    if (failure.code === 'OTP_INVALID') {
      nextErrors.code = t('recovery.otpInvalid');
    }
    setErrors(nextErrors);
    const message = passwordErrorMessage(failure, t, i18n.language);
    if (message && failure.code !== 'CURRENT_PASSWORD_INVALID' && failure.code !== 'PASSWORD_SAME' && failure.code !== 'OTP_INVALID') {
      toast(message, 'error');
    }
  };

  const submit = async () => {
    if (submitLock.current) {
      return;
    }
    submitLock.current = true;

    const outcome = await run({
      purpose: 'password.change',
      required: needsOtp,
      request: async (factor) => {
        const next = await changePassword({
          currentPassword,
          newPassword,
          secondFactor: factor,
        });
        if (!next.success && (next.failure?.code === 'OTP_INVALID' || next.failure?.code === 'OTP_REQUIRED')) {
          throw new ApiError('otp', 400, {
            errors: [{ code: next.failure.code, methods: ['totp', 'backup'], purpose: 'password.change' }],
          });
        }
        return next;
      },
    });
    if (!outcome.ok) {
      return;
    }
    const result = outcome.data;

    if (result.success) {
      const datetime = formatDateTime(result.user?.withdrawalsLockedUntil ?? '', i18n.language);
      toast(t('recovery.changeSuccess', { datetime }), 'success');
      handleOpenChange(false);
      return;
    }

    submitLock.current = false;
    if (result.failure) {
      applyFailure(result.failure);
      return;
    }
    toast(t('recovery.requestFailed'), 'error');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('recovery.changeTitle')}</DialogTitle>
        </DialogHeader>

        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (validate()) {
              void submit();
            }
          }}
        >
            <PasswordField
              id="current-password"
              label={t('recovery.currentPassword')}
              value={currentPassword}
              shown={showCurrent}
              error={errors.currentPassword}
              disabled={locked}
              onToggle={() => setShowCurrent((value) => !value)}
              onChange={(value) => setCurrentPassword(value)}
            />
            <div className="space-y-2">
              <PasswordField
                id="new-password"
                label={t('recovery.newPassword')}
                value={newPassword}
                shown={showNew}
                error={errors.newPassword}
                disabled={locked}
                onToggle={() => setShowNew((value) => !value)}
                onChange={(value) => {
                  setNewPassword(value);
                  setPolicyRules([]);
                }}
              />
              <PasswordPolicyChecklist password={newPassword} failedRules={policyRules} />
            </div>
            <PasswordField
              id="repeat-password"
              label={t('recovery.repeatNewPassword')}
              value={repeatPassword}
              shown={false}
              error={errors.repeatPassword}
              disabled={locked}
              onChange={setRepeatPassword}
            />
            {needsOtp && (
              <div className="space-y-2">
                <Label htmlFor="change-password-code">{t('recovery.otpCode')}</Label>
                <Input
                  id="change-password-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  disabled={locked}
                  error={errors.code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
            )}

            <PasswordChangeWarning emphasizeCurrentToken={hasApiToken} />

            <DialogFooter className="px-0 pt-0 sm:[&_button]:w-full">
              <Button type="submit" className="w-full" loading={isSubmitting} disabled={secondsLeft > 0}>
                {secondsLeft > 0 ? t('recovery.lockedFor', { seconds: secondsLeft }) : t('recovery.submitReset')}
              </Button>
            </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  shown: boolean;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onToggle?: () => void;
}

function PasswordField({ id, label, value, shown, error, disabled, onChange, onToggle }: PasswordFieldProps) {
  return (
    <div className="relative space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={shown ? 'text' : 'password'}
        autoComplete="new-password"
        value={value}
        disabled={disabled}
        error={error}
        icon={<Lock className="h-4 w-4" />}
        onChange={(event) => onChange(event.target.value)}
      />
      {onToggle && (
        <button
          type="button"
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
          onClick={onToggle}
          aria-label={label}
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}
